import { type Static, Type } from "typebox";

import { DriverLinkToken } from "./drivers.js";
import {
  AttachmentCategory,
  AuditActorKind,
  AuditEntityType,
  ChangeActorType,
  FileLifecycleState,
  IssueAction,
  IssueCategory,
  IssueListSort,
  IssueReporterType,
  IssueSeverity,
  IssueStatus,
} from "./enums.js";
import { defineRoute } from "./route.js";
import {
  errors,
  Exact,
  Extend,
  Identifier,
  JsonRecord,
  ListOf,
  Nullable,
  Page,
  Timestamp,
} from "./schema.js";

// --- Issues ----------------------------------------------------------------

/** The most photos one report can carry. */
export const maxIssuePhotos = 10;

/** What the queue row and the detail page both show. */
export const IssueSummary = Exact({
  category: IssueCategory,
  description: Type.String(),
  id: Identifier,
  reportedAt: Timestamp,
  reporterName: Nullable(Type.String()),
  reporterType: Nullable(IssueReporterType),
  severity: IssueSeverity,
  status: IssueStatus,
  updatedAt: Timestamp,
  vehicleId: Identifier,
  vehicleLabel: Type.String(),
});
export type IssueSummary = Static<typeof IssueSummary>;

export const IssueListItem = Extend(IssueSummary, {
  photoCount: Type.Integer(),
  serviceRecordId: Nullable(Type.String()),
});
export type IssueListItem = Static<typeof IssueListItem>;

export const IssuePage = Page(IssueListItem);
export type IssuePage = Static<typeof IssuePage>;

export const IssuePhoto = Exact({
  id: Identifier,
  mimeType: Type.String(),
  originalFilename: Type.String(),
  /** Signed per request and short-lived; null without storage configured. */
  url: Nullable(Type.String()),
});
export type IssuePhoto = Static<typeof IssuePhoto>;

export const IssueTimelineEvent = Exact({
  actorName: Nullable(Type.String()),
  actorType: ChangeActorType,
  createdAt: Timestamp,
  fromStatus: Nullable(IssueStatus),
  id: Identifier,
  isCorrection: Type.Boolean(),
  note: Nullable(Type.String()),
  toStatus: IssueStatus,
});
export type IssueTimelineEvent = Static<typeof IssueTimelineEvent>;

export const IssueDetail = Extend(IssueSummary, {
  acknowledgedAt: Nullable(Timestamp),
  dismissedAt: Nullable(Timestamp),
  photos: Type.Array(IssuePhoto),
  resolutionNote: Nullable(Type.String()),
  resolvedAt: Nullable(Timestamp),
  scheduledAt: Nullable(Timestamp),
  scheduledFor: Nullable(Timestamp),
  /** The service that resolved it, when one did. */
  serviceRecord: Nullable(
    Exact({
      amountCents: Type.Integer(),
      completedAt: Timestamp,
      currency: Type.String(),
      id: Identifier,
    }),
  ),
  timeline: Type.Array(IssueTimelineEvent),
});
export type IssueDetail = Static<typeof IssueDetail>;

/** What a report answers with: enough to confirm it and link to it. */
export const ReportedIssue = Exact({
  category: IssueCategory,
  id: Identifier,
  photoFileIds: Type.Array(Identifier),
  reportedAt: Timestamp,
  severity: IssueSeverity,
  status: IssueStatus,
  vehicleId: Identifier,
});
export type ReportedIssue = Static<typeof ReportedIssue>;

const IssueReport = {
  category: IssueCategory,
  description: Type.String({ maxLength: 4_000, minLength: 1 }),
  photoFileIds: Type.Optional(
    Type.Array(Identifier, { maxItems: maxIssuePhotos }),
  ),
  severity: IssueSeverity,
};

export const reportIssue = defineRoute({
  body: Exact({ ...IssueReport, vehicleId: Identifier }),
  method: "POST",
  path: "/organizations/:organizationId/issues",
  // 409 when the vehicle has been sold or archived.
  response: { 201: ReportedIssue, ...errors(401, 403, 404, 409, 422) },
  tags: ["issues"],
});

export const reportDriverIssue = defineRoute({
  // A driver with one vehicle need not name it.
  body: Exact({ ...IssueReport, vehicleId: Type.Optional(Identifier) }),
  method: "POST",
  params: Exact({ token: DriverLinkToken }),
  path: "/driver-links/:token/issues",
  response: { 201: ReportedIssue, ...errors(403, 404, 409, 422, 429) },
  tags: ["driver-links"],
});

export const listIssues = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/issues",
  querystring: Exact({
    cursor: Type.Optional(Type.String({ maxLength: 400 })),
    pageSize: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
    reportedFrom: Type.Optional(Type.String({ format: "date-time" })),
    reportedTo: Type.Optional(Type.String({ format: "date-time" })),
    search: Type.Optional(Type.String({ maxLength: 200 })),
    // Repeatable: ?status=open&status=acknowledged
    severity: Type.Optional(Type.Array(IssueSeverity)),
    sort: Type.Optional(IssueListSort),
    status: Type.Optional(Type.Array(IssueStatus)),
    vehicleId: Type.Optional(Identifier),
  }),
  response: { 200: IssuePage, ...errors(401, 403, 422) },
  tags: ["issues"],
});

export const getIssue = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/issues/:issueId",
  response: { 200: IssueDetail, ...errors(401, 403, 404) },
  tags: ["issues"],
});

export const transitionIssue = defineRoute({
  body: Exact({
    action: IssueAction,
    note: Type.Optional(Type.String({ maxLength: 4_000 })),
    scheduledFor: Type.Optional(Type.String({ format: "date-time" })),
    serviceRecordId: Type.Optional(Identifier),
  }),
  method: "POST",
  path: "/organizations/:organizationId/issues/:issueId/transitions",
  response: { 200: IssueDetail, ...errors(401, 403, 404, 409, 422) },
  tags: ["issues"],
});

// --- Files ----------------------------------------------------------------

/** A signed URL to put the file to, and the id to finalize it with. */
export const UploadAuthorization = Exact({
  expiresAt: Timestamp,
  id: Identifier,
  uploadUrl: Type.String(),
});
export type UploadAuthorization = Static<typeof UploadAuthorization>;

export const FileSummary = Exact({
  id: Identifier,
  mimeType: Type.String(),
  originalFilename: Type.String(),
  sizeBytes: Type.Integer(),
});
export type FileSummary = Static<typeof FileSummary>;

/** A finalized file as it is stored. */
export const StoredFile = Extend(FileSummary, {
  attachmentCategory: Nullable(AttachmentCategory),
  bucket: Type.String(),
  checksum: Type.String(),
  lifecycleState: FileLifecycleState,
  organizationId: Identifier,
  storageKey: Type.String(),
  uploadedByDriverId: Nullable(Type.String()),
  uploadedByUserId: Nullable(Type.String()),
});
export type StoredFile = Static<typeof StoredFile>;

const FileDescription = {
  mimeType: Type.String({ maxLength: 120, minLength: 1 }),
  originalFilename: Type.String({ maxLength: 255, minLength: 1 }),
  sizeBytes: Type.Integer({ maximum: 20 * 1024 * 1024, minimum: 0 }),
};

export const createUpload = defineRoute({
  body: Exact({ attachmentCategory: AttachmentCategory, ...FileDescription }),
  method: "POST",
  path: "/organizations/:organizationId/uploads",
  response: { 201: UploadAuthorization, ...errors(401, 403, 422) },
  tags: ["files"],
});

export const finalizeUpload = defineRoute({
  method: "POST",
  path: "/organizations/:organizationId/uploads/:uploadId/finalize",
  response: { 201: StoredFile, ...errors(401, 403, 404, 409, 422) },
  tags: ["files"],
});

/** A driver's link uploads issue photos and nothing else. */
export const createDriverUpload = defineRoute({
  body: Exact(FileDescription),
  method: "POST",
  params: Exact({ token: DriverLinkToken }),
  path: "/driver-links/:token/uploads",
  response: { 201: UploadAuthorization, ...errors(403, 404, 422, 429) },
  tags: ["driver-links"],
});

export const finalizeDriverUpload = defineRoute({
  method: "POST",
  params: Exact({ token: DriverLinkToken, uploadId: Identifier }),
  path: "/driver-links/:token/uploads/:uploadId/finalize",
  // The driver needs the id to attach it, not the storage coordinates.
  response: { 201: FileSummary, ...errors(403, 404, 409, 422, 429) },
  tags: ["driver-links"],
});

// --- Audit ----------------------------------------------------------------

export const AuditEvent = Exact({
  action: Type.String(),
  actorId: Nullable(Type.String()),
  actorName: Nullable(Type.String()),
  actorType: Type.String(),
  // What changed, as recorded. The keys depend on the kind of thing, so these
  // are the one place the contract describes an object without its fields.
  after: Nullable(JsonRecord),
  before: Nullable(JsonRecord),
  createdAt: Timestamp,
  driverId: Nullable(Type.String()),
  driverName: Nullable(Type.String()),
  entityId: Type.String(),
  entityType: Type.String(),
  id: Identifier,
  metadata: JsonRecord,
  subjectName: Nullable(Type.String()),
  vehicleId: Nullable(Type.String()),
  vehicleName: Nullable(Type.String()),
});
export type AuditEvent = Static<typeof AuditEvent>;

export const AuditEventPage = Page(AuditEvent);
export type AuditEventPage = Static<typeof AuditEventPage>;

export const listAuditEvents = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/audit-events",
  querystring: Exact({
    // Repeatable. A verb (created, updated, deleted) or a full action.
    action: Type.Optional(
      Type.Array(Type.String({ maxLength: 100, pattern: "^[a-z_.]+$" }), {
        maxItems: 20,
      }),
    ),
    actorId: Type.Optional(Type.String({ maxLength: 100 })),
    cursor: Type.Optional(Type.String({ maxLength: 200 })),
    entityId: Type.Optional(Type.String({ maxLength: 100 })),
    entityType: Type.Optional(AuditEntityType),
    from: Type.Optional(Type.String({ format: "date-time" })),
    // Repeatable: the kinds of thing, such as vehicle or issue.
    kind: Type.Optional(
      Type.Array(Type.String({ maxLength: 60, pattern: "^[a-z_]+$" }), {
        maxItems: 20,
      }),
    ),
    pageSize: Type.Optional(Type.Integer({ maximum: 100, minimum: 1 })),
    relatedDriverId: Type.Optional(Identifier),
    relatedVehicleId: Type.Optional(Identifier),
    to: Type.Optional(Type.String({ format: "date-time" })),
  }),
  response: { 200: AuditEventPage, ...errors(401, 403) },
  tags: ["audit"],
});

export const AuditActor = Exact({
  id: Type.String(),
  kind: AuditActorKind,
  name: Type.String(),
});
export type AuditActor = Static<typeof AuditActor>;

export const AuditActorList = ListOf(AuditActor);
export type AuditActorList = Static<typeof AuditActorList>;

export const listAuditActors = defineRoute({
  method: "GET",
  path: "/organizations/:organizationId/audit-events/actors",
  response: { 200: AuditActorList, ...errors(401, 403) },
  tags: ["audit"],
});
